import { useEffect, useMemo, useState } from "react";
import { hasRole, useApi, useApp } from "../context/AppContext";

// The purchase's basket as the sales platform holds it, and an operator's
// correction of it (docs/tasks/37): after looking at the crops and the
// review, a tenant admin sets the basket to what was really taken - also after
// the customer paid. The platform decides what the money does: an open receipt
// is simply replaced; a Stripe payment is refunded the difference; a basket
// that should cost more is recorded as outstanding, because a card tapped on
// the tablet cannot be charged again without the customer.

function money(cents, currency = "eur") {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency || "eur").toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency || ""}`;
  }
}

const PAYMENT = {
  open: "Receipt open - not charged yet",
  capture_pending: "Being charged right now",
  captured: "Charged",
  cancelled: "Hold released - nothing charged",
  service: "Service card - no payment",
  manual: "Payment needs a look",
  none: "No payment",
};

function newRequestId() {
  return (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
}

// What a correction will do (or did), in the operator's words.
export function planText(a, card) {
  if (!a) return "";
  const cur = a.currency;
  if (a.status === "failed") return `The refund failed and the basket was put back as it was: ${a.message || "no reason given"}. Nothing was refunded.`;
  if (a.reason === "capture_pending") return "The payment is being captured right now. Try again in a few minutes.";
  if (a.reason === "busy") return "Another correction of this basket is being applied. Reload and try again.";
  if (a.action === "refuse") return `Cannot be corrected: ${a.reason}.`;
  if (a.action === "refund") {
    return `Refund ${money(a.refund, cur)} to the card${card ? ` ${card.slice(-8)}` : ""}: charged ${money(a.charged, cur)}, corrected ${money(a.amount_after, cur)}. The customer does nothing; the refund reaches the card in a few days.`;
  }
  if (a.action === "outstanding") {
    return `The customer owes ${money(a.outstanding, cur)} more. A card tapped on the tablet cannot be charged again without the customer, so nothing is charged: the correction is recorded and the receipt keeps what was paid.`;
  }
  if (a.action === "manual") return `Recorded only: this payment (${a.reason || "unknown"}) is not refunded automatically - settle it by hand.`;
  if (a.action === "replace" && a.payment === "open") {
    return `The receipt is still open: the basket becomes ${money(a.amount_after, cur)} and is charged at the normal close.${a.over_preauth ? ` That is more than the card hold of ${money(a.over_preauth, cur)}: the capture will fail - settle the rest by hand.` : ""}`;
  }
  if (a.action === "replace") return `The lines change; the amount stays ${money(a.amount_after, cur)} and no money moves.`;
  return JSON.stringify(a);
}

function History({ basket, ours }) {
  const rows = [
    ...(basket?.adjustments || []).map((a) => ({ at: a.at, who: a.reason === "dispute" ? "customer dispute" : `cloud check (${a.reason})`,
      text: `${money(a.amount_before)} → ${money(a.amount_after)}` })),
    ...(basket?.corrections || []).map((c) => ({ at: c.at, who: c.operator?.email || "operator", note: c.note,
      text: `${planText(c, basket?.payment?.card)}${c.status === "failed" ? ` - the refund FAILED: ${c.message || ""}` : c.status === "pending" ? " - pending" : ""}` })),
  ].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  // Our own records of refused attempts (the platform keeps only what it applied).
  const refused = (ours || []).filter((c) => c.answer?.status !== 200);
  if (!rows.length && !refused.length) return null;
  return (
    <div>
      <div className="label">Changes after the cabinet</div>
      <ul className="space-y-1 text-xs">
        {rows.map((r, i) => (
          <li key={i}><span className="text-slate-500">{r.at ? new Date(r.at).toLocaleString() : ""} · {r.who}:</span> {r.text}{r.note ? <span className="italic text-slate-600"> “{r.note}”</span> : null}</li>
        ))}
        {refused.map((c) => (
          <li key={c.request_id} className="text-amber-800">
            <span className="text-slate-500">{new Date(c.created_at * 1000).toLocaleString()} · {c.operator?.email}:</span> not applied ({c.answer?.status}) - {planText(c.answer?.body) || c.answer?.body?.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Editor({ sessionId, basket, onDone, onCancel }) {
  const api = useApi();
  const [rows, setRows] = useState(() => (basket?.lines || []).map((l) => ({ ean_code: l.gtin, name: l.name, quantity: l.quantity, price: l.price })));
  const [products, setProducts] = useState([]);
  const [pick, setPick] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.listProducts().then((list) => setProducts((list || []).filter((p) => p.ean_code && /^\d{8,14}$/.test(p.ean_code)))).catch(() => setProducts([]));
  }, [api]);

  const lines = useMemo(() => rows.filter((r) => r.quantity > 0).map((r) => (r.product_uuid
    ? { product_uuid: r.product_uuid, quantity: r.quantity } : { ean_code: r.ean_code, quantity: r.quantity })), [rows]);
  const key = JSON.stringify(lines);
  const setQty = (i, q) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, quantity: Math.max(0, Math.min(50, q)) } : r)));
  const add = () => {
    const p = products.find((x) => x.id === pick);
    if (!p) return;
    setRows((rs) => {
      const i = rs.findIndex((r) => r.ean_code === p.ean_code);
      if (i >= 0) return rs.map((r, k) => (k === i ? { ...r, quantity: r.quantity + 1 } : r));
      return [...rs, { product_uuid: p.id, ean_code: p.ean_code, name: p.product_name || p.ean_code, quantity: 1 }];
    });
    setPick("");
  };

  const send = async (dryRun) => {
    if (note.trim().length < 3) { setErr("Say why - what the crops or the data show."); return; }
    setBusy(true); setErr(null);
    try {
      const body = { request_id: newRequestId(), note: note.trim(), lines, dry_run: dryRun };
      if (!dryRun && !window.confirm(`${planText(preview, basket?.payment?.card)}\n\nApply this correction?`)) { setBusy(false); return; }
      const a = await api.correctPurchase(sessionId, body);
      if (dryRun) { setPreview(a); setPreviewKey(key); } else onDone(a);
    } catch (e) {
      const a = e.detail && typeof e.detail === "object" && !e.detail.error ? e.detail : null;
      if (a) { setPreview(a); setPreviewKey(key); }
      setErr(a ? planText(a, basket?.payment?.card) : String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded border border-slate-200 p-3">
      <div className="label">What was really taken</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.ean_code}-${i}`} className={r.quantity === 0 ? "text-slate-400 line-through" : ""}>
              <td className="py-1">{r.name || r.ean_code} <span className="font-mono text-xs text-slate-500">{r.ean_code}</span></td>
              <td className="w-36 py-1 text-right">
                <button className="btn btn-secondary px-2 py-0 text-xs" onClick={() => setQty(i, r.quantity - 1)}>−</button>
                <span className="inline-block w-8 text-center font-mono">{r.quantity}</span>
                <button className="btn btn-secondary px-2 py-0 text-xs" onClick={() => setQty(i, r.quantity + 1)}>+</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2">
        <select className="input flex-1 text-sm" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Add a product…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.product_name || p.ean_code} · {p.ean_code}</option>)}
        </select>
        <button className="btn btn-secondary text-xs" disabled={!pick} onClick={add}>Add</button>
      </div>
      <textarea className="input w-full text-sm" rows={2} placeholder="Why: what the crops / the data show (kept with the correction)" value={note} onChange={(e) => setNote(e.target.value)} />
      {preview && previewKey === key && <p className="rounded bg-sky-50 px-2 py-1 text-xs text-sky-900">{planText(preview, basket?.payment?.card)}</p>}
      {err && <p className="text-xs text-rose-700">{err}</p>}
      <div className="flex gap-2">
        <button className="btn btn-secondary text-xs" disabled={busy} onClick={() => send(true)}>Preview</button>
        <button className="btn btn-primary text-xs" disabled={busy || !preview || previewKey !== key || preview.action === "refuse"}
          onClick={() => send(false)}>Apply</button>
        <button className="btn btn-secondary text-xs" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      <p className="text-[11px] text-slate-500">Preview asks the sales platform what would happen and changes nothing. Apply is recorded with your name and note.</p>
    </div>
  );
}

export function BasketCorrection({ sessionId }) {
  const api = useApi();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [done, setDone] = useState(null);
  const load = () => {
    setErr(null);
    api.getPurchaseBasket(sessionId).then(setData).catch((e) => setErr(String(e.message || e)));
  };
  useEffect(load, [api, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <p className="text-xs text-rose-700">Basket: {err}</p>;
  if (!data) return <p className="text-xs text-slate-500">Loading the basket…</p>;
  const b = data.basket;
  const pay = b?.payment || {};
  const cur = pay.currency;
  return (
    <div className="space-y-3">
      <div>
        <div className="label">Basket on the sales platform</div>
        {!b ? <p className="text-xs text-amber-800">Not available: {data.basket_error}</p> : (
          <>
            <p className="text-xs text-slate-600">
              {PAYMENT[pay.state] || pay.state}{pay.card ? ` · ${pay.brand || "card"} ${pay.card.slice(-8)}` : ""}
              {pay.captured != null ? ` · charged ${money(pay.captured, cur)}${pay.refunded ? `, refunded ${money(pay.refunded, cur)}` : ""}` : ""}
              {pay.state === "open" && pay.hold ? ` · card hold ${money(pay.hold, cur)}` : ""}
            </p>
            <table className="mt-1 w-full text-sm">
              <tbody>
                {b.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.quantity}× {l.name || l.gtin} <span className="font-mono text-xs text-slate-500">{l.gtin}</span>{l.unpriced ? <span className="text-xs text-amber-700"> · no price</span> : null}</td>
                    <td className="text-right font-mono text-xs">{money((l.price || 0) * (l.quantity || 0), cur)}</td>
                  </tr>
                ))}
                <tr className="border-t"><td className="text-xs text-slate-500">Total</td><td className="text-right font-mono text-sm">{money(b.amount, cur)}</td></tr>
              </tbody>
            </table>
          </>
        )}
      </div>
      <History basket={b} ours={data.corrections} />
      {done && <p className="rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-800">Applied. {planText(done, pay.card)}</p>}
      {b && hasRole(user, "tenant_admin") && (editing
        ? <Editor sessionId={sessionId} basket={b} onCancel={() => setEditing(false)} onDone={(a) => { setDone(a); setEditing(false); load(); }} />
        : <button className="btn btn-secondary text-xs" onClick={() => { setDone(null); setEditing(true); }}>Correct the basket</button>)}
    </div>
  );
}
